/**
 * TimelineRuler component
 * コンテンツタイムラインレイアウト用の時間軸目盛り
 */

import React, { useEffect, useState } from 'react';
import { useReactFlow, Position, useStore } from '@xyflow/react';
import { secondsToTimestamp } from '@/types/contentTypes';

interface TimelineRulerProps {
  startTime?: number;
  endTime?: number;
  height?: number;
}

/**
 * タイムライン目盛りコンポーネント
 */
const TimelineRuler: React.FC<TimelineRulerProps> = ({
  startTime = 0,
  endTime = 3600, // デフォルト1時間
  height = 40
}) => {
  const { getNodes } = useReactFlow();
  const [timePoints, setTimePoints] = useState<Array<{ time: number, x: number, label: string }>>([]);
  
  // ビューポートが変わったときに目盛りを更新
  const transform = useStore((state) => state.transform);
  
  useEffect(() => {
    // コンテンツノードをフィルタリング
    const contentNodes = getNodes().filter(node => node.type === 'contentNode');
    
    if (contentNodes.length === 0) {
      return;
    }
    
    // ノードからタイムスタンプを取得してソート
    const timestamps = contentNodes
      .map(node => {
        const timestampSecs = node.data?.timestampSeconds;
        return typeof timestampSecs === 'number' ? timestampSecs : 0;
      })
      .filter(ts => ts > 0)
      .sort((a, b) => a - b);
    
    // タイムスタンプがなければ終了
    if (timestamps.length === 0) {
      return;
    }
    
    // 実際の開始時間と終了時間
    const actualStartTime = timestamps[0];
    const actualEndTime = timestamps[timestamps.length - 1];
    
    // 時間範囲
    const duration = actualEndTime - actualStartTime;
    
    // 目盛りの間隔を決定（適切な数の目盛りが表示されるように）
    let interval = 60; // デフォルト1分間隔
    
    if (duration > 3600) {
      interval = 600; // 10分間隔
    } else if (duration > 1800) {
      interval = 300; // 5分間隔
    } else if (duration > 600) {
      interval = 120; // 2分間隔
    }
    
    // 最初の目盛りを計算（interval の倍数に揃える）
    const firstTick = Math.ceil(actualStartTime / interval) * interval;
    
    // 目盛りポイントを生成
    const points = [];
    
    // ビューポートのサイズを取得できないため、window サイズを使用
    const viewportWidth = window.innerWidth;
    
    for (let time = firstTick; time <= actualEndTime; time += interval) {
      // タイムスタンプの位置をX座標に変換
      const ratio = (time - actualStartTime) / duration;
      const x = (ratio * viewportWidth * 0.8) + (viewportWidth * 0.1); // 左右に10%マージン
      
      points.push({
        time,
        x,
        label: secondsToTimestamp(time)
      });
    }
    
    setTimePoints(points);
  }, [getNodes, transform]);
  
  return (
    <div 
      className="absolute left-0 top-0 w-full" 
      style={{ height: `${height}px` }}
    >
      <div className="relative w-full h-full bg-background/50 backdrop-blur-sm border-b">
        {/* 目盛り線と時間ラベル */}
        {timePoints.map((point, index) => (
          <div 
            key={index} 
            className="absolute top-0 h-full"
            style={{ left: `${point.x}px` }}
          >
            <div className="absolute bottom-0 w-px h-3 bg-border" />
            <div className="absolute bottom-4 text-xs text-muted-foreground transform -translate-x-1/2">
              {point.label}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default TimelineRuler;